import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  Search,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Eye,
  AlertTriangle,
  Package,
  Tag,
  Percent,
  IndianRupee,
  Info,
  ChevronRight,
  ChevronUp,
  CalendarDays,
  Hash,
  Layers,
} from "lucide-react";

import Pagination from "../../../Component/Common/Pagination";
import RefreshButton from "../../../Component/Common/RefreshButton";
import Badge from "../../../Component/RetUserComponents/Badge";
import { BASE_URL } from "../../../config/api";
import Modal from "./Modal";
import classNames from "./classNames";
import Chip from "./Chip";
import ProductImg from "./ProductImg";
import DetailRow from "./DetailRow";
import { fmt } from "./fmt";
import ProductDetailsModal from "./ProductDetailsModal";
import ConfirmActionModal from "./ConfirmActionModal";
import RegularLoader from "../../../Component/Loader/RegularLoader";

// ✅ SET BASE_URL (or import from config)

const money = (n) => `₹${Number(n || 0).toLocaleString()}`;

function statusBadgeVariantPublished(status) {
  return Number(status) === 1 ? "success" : "warning";
}
function companyActiveBadge(p_status) {
  return Number(p_status) === 1 ? "success" : "danger";
}

const pricingTypeLabel = (p) => {
  const isPercent = Number(p?.is_percent) === 1;
  const isFixed = Number(p?.is_fixed) === 1;
  return isPercent ? "Percent" : isFixed ? "Fixed" : "—";
};
const mrpLabel = (p) => (Number(p?.is_percent) === 1 ? `${fmt(p?.mrp)}%` : money(p?.mrp));
const marginLabel = (p, margin) => (Number(p?.is_percent) === 1 ? `${fmt(margin)}%` : money(margin));
const rangeLabel = (p) => `${money(p?.min_value)} – ${money(p?.max_value)}`;

// ✅ normalize "company product row" shape to { company_product_id, product_id, margin, p_status, product:{...} }
function normalizeCompanyProductRow(r) {
  return {
    company_product_id: r.company_product_id,
    product_id: r.product_id,
    company_id: r.company_id,
    margin: r.margin,
    p_status: r.p_status,
    product: r.product || null,
  };
}

// ✅ normalize master product into "row-like" object for unassigned list
function normalizeMasterProductRow(p, companyId) {
  return {
    company_product_id: null, // not assigned yet
    product_id: p.id,
    company_id: Number(companyId),
    margin: 0, // no company margin yet
    p_status: 0,
    product: p,
  };
}

export default function AssignCompanyProducts() {
  const { id } = useParams(); // company_id
  const location = useLocation();

  const companyObj = location.state?.c || location.state?.company || null;
  const companyName =
    companyObj?.business_name || companyObj?.name || location.state?.companyName || "Company";

  // UI state
  const [tab, setTab] = useState("assigned"); // assigned | unassigned
  const [search, setSearch] = useState("");
  const [published, setPublished] = useState("all");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedId, setExpandedId] = useState(null);

  const [openDetails, setOpenDetails] = useState(false);
  const [selected, setSelected] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, row }

  // API state
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // data
  const [companyProducts, setCompanyProducts] = useState([]); // rows from company-products API (flattened for this company)
  const [allProducts, setAllProducts] = useState([]); // master products-with-categories

  useEffect(() => setCurrentPage(1), [tab, search, published, pageSize]);

  // ✅ Fetch both APIs and build lists
  // const fetchData = async () => {
  //   try {
  //     setLoading(true);
  //     setErrorMsg("");

  //     // ✅ Add auth header if required
  //     const headers = {
  //       "Content-Type": "application/json",
  //       // Authorization: `Bearer ${token}`,
  //     };

  //     // 1) company-products?company_id=ID
  //     const resCompany = await fetch(`${BASE_URL}warranty/company-products?company_id=${id}`, {
  //       method: "GET",
  //       headers,
  //     });
  //     const jsonCompany = await resCompany.json();
  //     if (!resCompany.ok) throw new Error(jsonCompany?.message || "Failed to load company products");

  //     // jsonCompany.data is an array of groups; find matching company_id group
  //     const group = Array.isArray(jsonCompany?.data)
  //       ? jsonCompany.data.find((g) => Number(g.company_id) === Number(id))
  //       : null;

  //     const cp = (group?.products || []).map(normalizeCompanyProductRow);
  //     setCompanyProducts(cp);

  //     // 2) products-with-categories (master list)
  //     const resAll = await fetch(`${BASE_URL}warranty/products-with-categories`, {
  //       method: "GET",
  //       headers,
  //     });
  //     const jsonAll = await resAll.json();
  //     if (!resAll.ok) throw new Error(jsonAll?.message || "Failed to load products");

  //     setAllProducts(Array.isArray(jsonAll?.products) ? jsonAll.products : []);
  //   } catch (e) {
  //     setErrorMsg(e?.message || "Something went wrong");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchData = async () => {
  try {
    setLoading(true);
    setErrorMsg("");

    const headers = {
      "Content-Type": "application/json",
    };

    // 1️⃣ Fetch master products FIRST
    const resAll = await fetch(`${BASE_URL}warranty/products-with-categories`, {
      method: "GET",
      headers,
    });
    const jsonAll = await resAll.json();
    if (!resAll.ok) throw new Error(jsonAll?.message || "Failed to load products");

    const masterProducts = Array.isArray(jsonAll?.products)
      ? jsonAll.products
      : [];
    setAllProducts(masterProducts);

    // Create lookup map
    const productMap = {};
    masterProducts.forEach((p) => {
      productMap[Number(p.id)] = p;
    });

    // 2️⃣ Fetch company products
    const resCompany = await fetch(
      `${BASE_URL}warranty/company-products?company_id=${id}`,
      { method: "GET", headers }
    );
    const jsonCompany = await resCompany.json();
    if (!resCompany.ok)
      throw new Error(jsonCompany?.message || "Failed to load company products");

    const group = Array.isArray(jsonCompany?.data)
      ? jsonCompany.data.find((g) => Number(g.company_id) === Number(id))
      : null;

    // 🔥 Merge product details
    const cp = (group?.products || []).map((r) =>
      normalizeCompanyProductRow({
        ...r,
        product: productMap[Number(r.product_id)] || null,
      })
    );

    setCompanyProducts(cp);
  } catch (e) {
    setErrorMsg(e?.message || "Something went wrong");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const assignedRows = useMemo(() => {
    return companyProducts.filter(
      (r) => r.company_product_id !== null
    );
  }, [companyProducts]);



  const unassignedRows = useMemo(() => {
    // ✅ Not Assigned:
    // master list - any product_id present in companyProducts (regardless p_status)
    const companyProductIds = new Set(companyProducts.map((r) => Number(r.product_id)));

    return allProducts
      .filter((p) => !companyProductIds.has(Number(p.id)))
      .map((p) => normalizeMasterProductRow(p, id));
  }, [allProducts, companyProducts, id]);

  const workingSet = tab === "assigned" ? assignedRows : unassignedRows;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workingSet.filter((r) => {
      const p = r.product || {};
      if (published === "published" && Number(p.status) !== 1) return false;
      if (published === "unpublished" && Number(p.status) === 1) return false;

      if (q) {
        const categories = Array.isArray(p.categories)
          ? p.categories.map((c) => c?.name).filter(Boolean).join(" ")
          : "";

        const hay = [p.name, p.hsn_code, p.zoho_id, r.product_id, r.company_product_id, p.features, categories]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [workingSet, search, published]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const pCounts = useMemo(
    () => ({
      assigned: assignedRows.length,
      unassigned: unassignedRows.length,
    }),
    [assignedRows.length, unassignedRows.length]
  );

  // ✅ Actions (UI confirm; you can connect POST/PUT API later)
  const requestAssign = (row) => {
    setConfirmAction({ type: "assign", row });
    setConfirmOpen(true);
  };
  const requestUnassign = (row) => {
    setConfirmAction({ type: "unassign", row });
    setConfirmOpen(true);
  };

  const runConfirm = async (payloadFromModal = {}) => {
    if (!confirmAction) return;

    try {
      setLoading(true);

      /* =========================
        TOGGLE PRODUCT STATUS (PUT)
      ========================== */
      if (confirmAction.type === "unassign") {
        const { company_product_id, p_status } = confirmAction.row;

        // 🔥 Toggle logic
        const nextStatus = Number(p_status) === 1 ? 0 : 1;

        const res = await fetch(
          `${BASE_URL}warranty/company-product/status/${company_product_id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              p_status: nextStatus,
            }),
          }
        );

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || "Failed to update product status");
        }
      }

      /* =========================
        ASSIGN PRODUCT (POST)
      ========================== */
      if (confirmAction.type === "assign") {
        const { row } = confirmAction;

        const payload = {
          product_ids: [row.product_id],
          company_id: Number(id),

          // ✅ modal → row → 0 (SAFE)
          margin:
            payloadFromModal.margin ??
            row.margin ??
            0,

          min_value:
            payloadFromModal.min_value ??
            row.product?.min_value ??
            0,

          max_value:
            payloadFromModal.max_value ??
            row.product?.max_value ??
            0,
        };

        const res = await fetch(`${BASE_URL}warranty/assign-product`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || "Failed to assign product");
        }
      }


      // ✅ SUCCESS
      setConfirmOpen(false);
      setConfirmAction(null);
      setExpandedId(null);

      await fetchData(); // refresh table

    } catch (err) {
      console.error(err);
      alert(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };




  const openProductDetails = (row) => {
    setSelected(row);
    setOpenDetails(true);
  };

  return (
    <div className="mx-auto space-y-4">
      {/* Tabs + counters */}
      <div className="flex justify-between gap-2 overflow-x-auto rounded-2xl border border-blue-100 bg-white p-2">
        <div className="flex gap-2">
          <Chip active={tab === "assigned"} onClick={() => setTab("assigned")}>
            Assigned
          </Chip>
          <Chip active={tab === "unassigned"} onClick={() => setTab("unassigned")}>
            Not Assigned
          </Chip>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
            Assigned: {pCounts.assigned}
          </span>
          <span className="rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
            Not Assigned: {pCounts.unassigned}
          </span>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
          >
            <RefreshButton />
            <span className="hidden md:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-6">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product / HSN / Zoho / category..."
                className="w-full rounded-xl border border-blue-100 bg-white py-2 pl-10 pr-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Published</label>
            <div className="relative">
              <select
                value={published}
                onChange={(e) => setPublished(e.target.value)}
                className="w-full appearance-none rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All</option>
                <option value="published">Published Only</option>
                <option value="unpublished">Unpublished Only</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-slate-600">Page Size</label>
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="w-full appearance-none rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div className="md:col-span-1 flex items-end">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setPublished("all");
              }}
              className="w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
            >
              Clear
            </button>
          </div>
        </div>

        {/* {loading ? (
          <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
            Loading products...
          </div>
        ) : null} */}

        {errorMsg ? (
          <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {errorMsg}
          </div>
        ) : null}
      </div>

      {/* Minimal table */}
      <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
        <div className="mb-3">
          <p className="text-base font-semibold text-slate-900">
            Products • <span className="text-blue-700">{companyName}</span>
          </p>
          <p className="mt-1 text-xs font-semibold text-slate-500">
            Showing <span className="font-semibold text-slate-900">{filtered.length}</span> results
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-center text-slate-700">
                <th className="py-3">Product</th>
                <th>Type</th>
                <th>MRP</th>
                <th>Margin</th>
                <th>Range</th>
                <th>Published</th>
                <th>Status</th>
                <th className="">Action</th>
              </tr>
            </thead>

            <tbody className="text-center">
              {loading && (
                <tr>
                  <td colSpan={7} className="py-10 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-semibold text-blue-700">
                      <RegularLoader/>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && pageRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-sm font-semibold text-slate-500">
                    No products found.
                  </td>
                </tr>
              ) : null}

              {!loading &&
                pageRows.map((r) => {
                const p = r.product || {};
                const rowKey = tab === "assigned" ? r.company_product_id : `u-${r.product_id}`;
                const expanded = expandedId === rowKey;

                return (
                  <React.Fragment key={rowKey}>
                    <tr className="border-b hover:bg-slate-50">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? null : rowKey)}
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-2 py-2 text-blue-700 hover:bg-blue-50"
                            title="Expand details"
                          >
                            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>

                          <ProductImg src={p.image} name={p.name} />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{fmt(p.name)}</p>
                            <p className="mt-0.5 text-xs font-semibold text-slate-500">
                              PID: <span className="font-semibold">{fmt(r.product_id)}</span>
                              {tab === "assigned" ? (
                                <>
                                  {" "}
                                  • CPID: <span className="font-semibold">{fmt(r.company_product_id)}</span>
                                </>
                              ) : null}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="font-semibold text-slate-900">{pricingTypeLabel(p)}</td>
                      <td className="font-semibold text-slate-900">{mrpLabel(p)}</td>
                      <td className="font-semibold text-slate-900">
                        {tab === "assigned" ? marginLabel(p, r.margin) : "—"}
                      </td>

                      <td className="font-semibold text-slate-700">
                        <span className="rounded-xl border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                          {rangeLabel(p)}
                        </span>
                      </td>

                      <td>
                        <Badge variant={statusBadgeVariantPublished(p.status)}>
                          {Number(p.status) === 1 ? "Published" : "Unpublished"}
                        </Badge>
                      </td>

                      <td>
                         <Badge variant={companyActiveBadge(r.p_status)}>
                           {Number(r.p_status) === 1 ? "Active" : "Inactive"}
                         </Badge>
                       </td>

                      <td className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? null : rowKey)}
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                          >
                            <Info className="h-4 w-4" />
                            View
                          </button>

                          <button
                            type="button"
                            onClick={() => openProductDetails(r)}
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4" />
                            Details
                          </button>

                          {tab === "assigned" ? (
                            <button
                              type="button"
                              onClick={() =>
                                Number(r.p_status) === 1
                                  ? requestUnassign(r)
                                  : requestAssign(r)
                              }
                              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition
                                ${
                                  Number(r.p_status) === 1
                                    ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                }
                              `}
                            >
                              {Number(r.p_status) === 1 ? (
                                <>
                                  <XCircle className="h-4 w-4" />
                                  Unassign
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="h-4 w-4" />
                                  Assign
                                </>
                              )}
                            </button>


                          ) : (
                            <button
                              type="button"
                              onClick={() => requestAssign(r)}
                              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Assign
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded details row */}
                    {expanded ? (
                      <tr className="border-b bg-slate-50/60">
                        <td colSpan={7} className="py-4">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <DetailRow icon={Hash} label="HSN Code" value={p.hsn_code} />
                            <DetailRow icon={Tag} label="Zoho ID" value={p.zoho_id} />
                            <DetailRow icon={CalendarDays} label="Validity (Days)" value={p.validity} />
                            <DetailRow icon={Package} label="Claims" value={p.claims} />
                            <DetailRow icon={Layers} label="Categories" value={(p.categories || []).map((c) => c?.name).filter(Boolean).join(", ") || "—"} />
                            {tab === "assigned" ? (
                              <DetailRow icon={Percent} label="Company Margin" value={marginLabel(p, r.margin)} />
                            ) : (
                              <DetailRow icon={Percent} label="Company Margin" value="—" />
                            )}
                            <div className="md:col-span-3 rounded-2xl border border-blue-100 bg-white p-4">
                              <p className="text-xs font-semibold text-slate-500">Features</p>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{fmt(p.features)}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-xs font-semibold text-slate-500">
              Page <span className="font-semibold text-slate-900">{currentPage}</span> of{" "}
              <span className="font-semibold text-slate-900">{totalPages}</span>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </div>

      {/* Details modal */}
      <ProductDetailsModal
        open={openDetails}
        selected={selected}
        tab={tab}
        onClose={() => {
          setOpenDetails(false);
          setSelected(null);
        }}
        mrpLabel={mrpLabel}
        marginLabel={marginLabel}
        rangeLabel={rangeLabel}
        requestAssign={requestAssign}
        requestUnassign={requestUnassign}
      />

      {/* Confirm modal */}
      <ConfirmActionModal
        open={confirmOpen}
        action={confirmAction}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmAction(null);
        }}
        onConfirm={runConfirm}
      />

    </div>
  );
}


// import React, { useEffect, useMemo, useState } from "react";
// import { useLocation, useParams } from "react-router-dom";

// import {
//   Search,
//   ChevronDown,
//   CheckCircle2,
//   XCircle,
//   Eye,
//   AlertTriangle,
//   Package,
//   Tag,
//   Percent,
//   IndianRupee,
//   Info,
//   ChevronRight,
//   ChevronUp,
//   CalendarDays,
//   Hash,
//   Layers,
// } from "lucide-react";

// import Pagination from "../../../Component/Common/Pagination";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import Badge from "../../../Component/RetUserComponents/Badge";
// import { BASE_URL } from "../../../config/api";
// import Modal from "./Modal";
// import classNames from "./classNames";
// import Chip from "./Chip";
// import ProductImg from "./ProductImg";
// import DetailRow from "./DetailRow";
// import { fmt } from "./fmt";
// import ProductDetailsModal from "./ProductDetailsModal";
// import ConfirmActionModal from "./ConfirmActionModal";
// import RegularLoader from "../../../Component/Loader/RegularLoader";

// // ✅ SET BASE_URL (or import from config)

// const money = (n) => `₹${Number(n || 0).toLocaleString()}`;

// function statusBadgeVariantPublished(status) {
//   return Number(status) === 1 ? "success" : "warning";
// }
// function companyActiveBadge(p_status) {
//   return Number(p_status) === 1 ? "success" : "danger";
// }

// const pricingTypeLabel = (p) => {
//   const isPercent = Number(p?.is_percent) === 1;
//   const isFixed = Number(p?.is_fixed) === 1;
//   return isPercent ? "Percent" : isFixed ? "Fixed" : "—";
// };
// const mrpLabel = (p) => (Number(p?.is_percent) === 1 ? `${fmt(p?.mrp)}%` : money(p?.mrp));
// const marginLabel = (p, margin) => (Number(p?.is_percent) === 1 ? `${fmt(margin)}%` : money(margin));
// const rangeLabel = (p) => `${money(p?.min_value)} – ${money(p?.max_value)}`;

// // ✅ normalize "company product row" shape to { company_product_id, product_id, margin, p_status, product:{...} }
// function normalizeCompanyProductRow(r) {
//   return {
//     company_product_id: r.company_product_id,
//     product_id: r.product_id,
//     company_id: r.company_id,
//     margin: r.margin,
//     p_status: r.p_status,
//     product: r.product || null,
//   };
// }

// // ✅ normalize master product into "row-like" object for unassigned list
// function normalizeMasterProductRow(p, companyId) {
//   return {
//     company_product_id: null, // not assigned yet
//     product_id: p.id,
//     company_id: Number(companyId),
//     margin: 0, // no company margin yet
//     p_status: 0,
//     product: p,
//   };
// }

// export default function AssignCompanyProducts() {
//   const { id } = useParams(); // company_id
//   const location = useLocation();

//   const companyObj = location.state?.c || location.state?.company || null;
//   const companyName =
//     companyObj?.business_name || companyObj?.name || location.state?.companyName || "Company";

//   // UI state
//   const [tab, setTab] = useState("assigned"); // assigned | unassigned
//   const [search, setSearch] = useState("");
//   const [published, setPublished] = useState("all");

//   const [pageSize, setPageSize] = useState(10);
//   const [currentPage, setCurrentPage] = useState(1);

//   const [expandedId, setExpandedId] = useState(null);

//   const [openDetails, setOpenDetails] = useState(false);
//   const [selected, setSelected] = useState(null);

//   const [confirmOpen, setConfirmOpen] = useState(false);
//   const [confirmAction, setConfirmAction] = useState(null); // { type, row }

//   // API state
//   const [loading, setLoading] = useState(false);
//   const [errorMsg, setErrorMsg] = useState("");

//   // data
//   const [companyProducts, setCompanyProducts] = useState([]); // rows from company-products API (flattened for this company)
//   const [allProducts, setAllProducts] = useState([]); // master products-with-categories

//   useEffect(() => setCurrentPage(1), [tab, search, published, pageSize]);

//   // ✅ Fetch both APIs and build lists
//   // const fetchData = async () => {
//   //   try {
//   //     setLoading(true);
//   //     setErrorMsg("");

//   //     // ✅ Add auth header if required
//   //     const headers = {
//   //       "Content-Type": "application/json",
//   //       // Authorization: `Bearer ${token}`,
//   //     };

//   //     // 1) company-products?company_id=ID
//   //     const resCompany = await fetch(`${BASE_URL}warranty/company-products?company_id=${id}`, {
//   //       method: "GET",
//   //       headers,
//   //     });
//   //     const jsonCompany = await resCompany.json();
//   //     if (!resCompany.ok) throw new Error(jsonCompany?.message || "Failed to load company products");

//   //     // jsonCompany.data is an array of groups; find matching company_id group
//   //     const group = Array.isArray(jsonCompany?.data)
//   //       ? jsonCompany.data.find((g) => Number(g.company_id) === Number(id))
//   //       : null;

//   //     const cp = (group?.products || []).map(normalizeCompanyProductRow);
//   //     setCompanyProducts(cp);

//   //     // 2) products-with-categories (master list)
//   //     const resAll = await fetch(`${BASE_URL}warranty/products-with-categories`, {
//   //       method: "GET",
//   //       headers,
//   //     });
//   //     const jsonAll = await resAll.json();
//   //     if (!resAll.ok) throw new Error(jsonAll?.message || "Failed to load products");

//   //     setAllProducts(Array.isArray(jsonAll?.products) ? jsonAll.products : []);
//   //   } catch (e) {
//   //     setErrorMsg(e?.message || "Something went wrong");
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const fetchData = async () => {
//   try {
//     setLoading(true);
//     setErrorMsg("");

//     const headers = {
//       "Content-Type": "application/json",
//     };

//     // 1️⃣ Fetch master products FIRST
//     const resAll = await fetch(`${BASE_URL}warranty/products-with-categories`, {
//       method: "GET",
//       headers,
//     });
//     const jsonAll = await resAll.json();
//     if (!resAll.ok) throw new Error(jsonAll?.message || "Failed to load products");

//     const masterProducts = Array.isArray(jsonAll?.products)
//       ? jsonAll.products
//       : [];
//     setAllProducts(masterProducts);

//     // Create lookup map
//     const productMap = {};
//     masterProducts.forEach((p) => {
//       productMap[Number(p.id)] = p;
//     });

//     // 2️⃣ Fetch company products
//     const resCompany = await fetch(
//       `${BASE_URL}warranty/company-products?company_id=${id}`,
//       { method: "GET", headers }
//     );
//     const jsonCompany = await resCompany.json();
//     if (!resCompany.ok)
//       throw new Error(jsonCompany?.message || "Failed to load company products");

//     const group = Array.isArray(jsonCompany?.data)
//       ? jsonCompany.data.find((g) => Number(g.company_id) === Number(id))
//       : null;

//     // 🔥 Merge product details
//     const cp = (group?.products || []).map((r) =>
//       normalizeCompanyProductRow({
//         ...r,
//         product: productMap[Number(r.product_id)] || null,
//       })
//     );

//     setCompanyProducts(cp);
//   } catch (e) {
//     setErrorMsg(e?.message || "Something went wrong");
//   } finally {
//     setLoading(false);
//   }
// };


//   useEffect(() => {
//     fetchData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [id]);

//   const assignedRows = useMemo(() => {
//     return companyProducts.filter(
//       (r) => r.company_product_id !== null
//     );
//   }, [companyProducts]);



//   const unassignedRows = useMemo(() => {
//     // ✅ Not Assigned:
//     // master list - any product_id present in companyProducts (regardless p_status)
//     const companyProductIds = new Set(companyProducts.map((r) => Number(r.product_id)));

//     return allProducts
//       .filter((p) => !companyProductIds.has(Number(p.id)))
//       .map((p) => normalizeMasterProductRow(p, id));
//   }, [allProducts, companyProducts, id]);

//   const workingSet = tab === "assigned" ? assignedRows : unassignedRows;

//   const filtered = useMemo(() => {
//     const q = search.trim().toLowerCase();
//     return workingSet.filter((r) => {
//       const p = r.product || {};
//       if (published === "published" && Number(p.status) !== 1) return false;
//       if (published === "unpublished" && Number(p.status) === 1) return false;

//       if (q) {
//         const categories = Array.isArray(p.categories)
//           ? p.categories.map((c) => c?.name).filter(Boolean).join(" ")
//           : "";

//         const hay = [p.name, p.hsn_code, p.zoho_id, r.product_id, r.company_product_id, p.features, categories]
//           .filter(Boolean)
//           .join(" ")
//           .toLowerCase();
//         if (!hay.includes(q)) return false;
//       }
//       return true;
//     });
//   }, [workingSet, search, published]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
//   const pageRows = useMemo(() => {
//     const start = (currentPage - 1) * pageSize;
//     return filtered.slice(start, start + pageSize);
//   }, [filtered, currentPage, pageSize]);

//   const pCounts = useMemo(
//     () => ({
//       assigned: assignedRows.length,
//       unassigned: unassignedRows.length,
//     }),
//     [assignedRows.length, unassignedRows.length]
//   );

//   // ✅ Actions (UI confirm; you can connect POST/PUT API later)
//   const requestAssign = (row) => {
//     setConfirmAction({ type: "assign", row });
//     setConfirmOpen(true);
//   };
//   const requestUnassign = (row) => {
//     setConfirmAction({ type: "unassign", row });
//     setConfirmOpen(true);
//   };

//   const runConfirm = async () => {
//     if (!confirmAction) return;

//     try {
//       setLoading(true);

//       /* =========================
//         TOGGLE PRODUCT STATUS (PUT)
//       ========================== */
//       if (confirmAction.type === "unassign") {
//         const { company_product_id, p_status } = confirmAction.row;

//         // 🔥 Toggle logic
//         const nextStatus = Number(p_status) === 1 ? 0 : 1;

//         const res = await fetch(
//           `${BASE_URL}warranty/company-product/status/${company_product_id}`,
//           {
//             method: "PUT",
//             headers: {
//               "Content-Type": "application/json",
//             },
//             body: JSON.stringify({
//               p_status: nextStatus,
//             }),
//           }
//         );

//         const data = await res.json();
//         if (!res.ok) {
//           throw new Error(data?.message || "Failed to update product status");
//         }
//       }

//       /* =========================
//         ASSIGN PRODUCT (POST)
//       ========================== */
//       if (confirmAction.type === "assign") {
//         const productId = confirmAction.row.product_id;
//         const product = confirmAction.row.product || {};

//         const payload = {
//           product_ids: [productId],
//           company_id: Number(id),
//           margin: product.margin || 0,
//           min_value: product.min_value || 0,
//           max_value: product.max_value || 0,
//         };

//         const res = await fetch(`${BASE_URL}warranty/assign-product`, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         });

//         const data = await res.json();
//         if (!res.ok) {
//           throw new Error(data?.message || "Failed to assign product");
//         }
//       }

//       // ✅ SUCCESS
//       setConfirmOpen(false);
//       setConfirmAction(null);
//       setExpandedId(null);

//       await fetchData(); // refresh table

//     } catch (err) {
//       console.error(err);
//       alert(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };




//   const openProductDetails = (row) => {
//     setSelected(row);
//     setOpenDetails(true);
//   };

//   return (
//     <div className="mx-auto space-y-4">
//       {/* Tabs + counters */}
//       <div className="flex justify-between gap-2 overflow-x-auto rounded-2xl border border-blue-100 bg-white p-2">
//         <div className="flex gap-2">
//           <Chip active={tab === "assigned"} onClick={() => setTab("assigned")}>
//             Assigned
//           </Chip>
//           <Chip active={tab === "unassigned"} onClick={() => setTab("unassigned")}>
//             Not Assigned
//           </Chip>
//         </div>

//         <div className="flex flex-wrap items-center gap-2">
//           <span className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
//             Assigned: {pCounts.assigned}
//           </span>
//           <span className="rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
//             Not Assigned: {pCounts.unassigned}
//           </span>
//           <button
//             type="button"
//             onClick={fetchData}
//             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//           >
//             <RefreshButton />
//             <span className="hidden md:inline">Refresh</span>
//           </button>
//         </div>
//       </div>

//       {/* Toolbar */}
//       <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
//         <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
//           <div className="md:col-span-6">
//             <label className="mb-1 block text-xs font-semibold text-slate-600">Search</label>
//             <div className="relative">
//               <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
//               <input
//                 value={search}
//                 onChange={(e) => setSearch(e.target.value)}
//                 placeholder="Search product / HSN / Zoho / category..."
//                 className="w-full rounded-xl border border-blue-100 bg-white py-2 pl-10 pr-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
//               />
//             </div>
//           </div>

//           <div className="md:col-span-3">
//             <label className="mb-1 block text-xs font-semibold text-slate-600">Published</label>
//             <div className="relative">
//               <select
//                 value={published}
//                 onChange={(e) => setPublished(e.target.value)}
//                 className="w-full appearance-none rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
//               >
//                 <option value="all">All</option>
//                 <option value="published">Published Only</option>
//                 <option value="unpublished">Unpublished Only</option>
//               </select>
//               <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
//             </div>
//           </div>

//           <div className="md:col-span-2">
//             <label className="mb-1 block text-xs font-semibold text-slate-600">Page Size</label>
//             <div className="relative">
//               <select
//                 value={pageSize}
//                 onChange={(e) => setPageSize(Number(e.target.value))}
//                 className="w-full appearance-none rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
//               >
//                 <option value={5}>5</option>
//                 <option value={10}>10</option>
//                 <option value={20}>20</option>
//                 <option value={50}>50</option>
//               </select>
//               <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
//             </div>
//           </div>

//           <div className="md:col-span-1 flex items-end">
//             <button
//               type="button"
//               onClick={() => {
//                 setSearch("");
//                 setPublished("all");
//               }}
//               className="w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//             >
//               Clear
//             </button>
//           </div>
//         </div>

//         {/* {loading ? (
//           <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
//             Loading products...
//           </div>
//         ) : null} */}

//         {errorMsg ? (
//           <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
//             {errorMsg}
//           </div>
//         ) : null}
//       </div>

//       {/* Minimal table */}
//       <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
//         <div className="mb-3">
//           <p className="text-base font-semibold text-slate-900">
//             Products • <span className="text-blue-700">{companyName}</span>
//           </p>
//           <p className="mt-1 text-xs font-semibold text-slate-500">
//             Showing <span className="font-semibold text-slate-900">{filtered.length}</span> results
//           </p>
//         </div>

//         <div className="overflow-x-auto">
//           <table className="w-full text-sm">
//             <thead>
//               <tr className="border-b text-center text-slate-700">
//                 <th className="py-3">Product</th>
//                 <th>Type</th>
//                 <th>MRP</th>
//                 <th>Margin</th>
//                 <th>Range</th>
//                 <th>Published</th>
//                 <th>Status</th>
//                 <th className="">Action</th>
//               </tr>
//             </thead>

//             <tbody className="text-center">
//               {loading && (
//                 <tr>
//                   <td colSpan={7} className="py-10 text-center">
//                     <div className="flex items-center justify-center gap-2 text-sm font-semibold text-blue-700">
//                       <RegularLoader/>
//                     </div>
//                   </td>
//                 </tr>
//               )}
//               {!loading && pageRows.length === 0 ? (
//                 <tr>
//                   <td colSpan={7} className="py-10 text-center text-sm font-semibold text-slate-500">
//                     No products found.
//                   </td>
//                 </tr>
//               ) : null}

//               {!loading &&
//                 pageRows.map((r) => {
//                 const p = r.product || {};
//                 const rowKey = tab === "assigned" ? r.company_product_id : `u-${r.product_id}`;
//                 const expanded = expandedId === rowKey;

//                 return (
//                   <React.Fragment key={rowKey}>
//                     <tr className="border-b hover:bg-slate-50">
//                       <td className="py-3">
//                         <div className="flex items-center gap-3">
//                           <button
//                             type="button"
//                             onClick={() => setExpandedId(expanded ? null : rowKey)}
//                             className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-2 py-2 text-blue-700 hover:bg-blue-50"
//                             title="Expand details"
//                           >
//                             {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
//                           </button>

//                           <ProductImg src={p.image} name={p.name} />
//                           <div className="min-w-0">
//                             <p className="truncate text-sm font-semibold text-slate-900">{fmt(p.name)}</p>
//                             <p className="mt-0.5 text-xs font-semibold text-slate-500">
//                               PID: <span className="font-semibold">{fmt(r.product_id)}</span>
//                               {tab === "assigned" ? (
//                                 <>
//                                   {" "}
//                                   • CPID: <span className="font-semibold">{fmt(r.company_product_id)}</span>
//                                 </>
//                               ) : null}
//                             </p>
//                           </div>
//                         </div>
//                       </td>

//                       <td className="font-semibold text-slate-900">{pricingTypeLabel(p)}</td>
//                       <td className="font-semibold text-slate-900">{mrpLabel(p)}</td>
//                       <td className="font-semibold text-slate-900">
//                         {tab === "assigned" ? marginLabel(p, r.margin) : "—"}
//                       </td>

//                       <td className="font-semibold text-slate-700">
//                         <span className="rounded-xl border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
//                           {rangeLabel(p)}
//                         </span>
//                       </td>

//                       <td>
//                         <Badge variant={statusBadgeVariantPublished(p.status)}>
//                           {Number(p.status) === 1 ? "Published" : "Unpublished"}
//                         </Badge>
//                       </td>

//                       <td>
//                          <Badge variant={companyActiveBadge(r.p_status)}>
//                            {Number(r.p_status) === 1 ? "Active" : "Inactive"}
//                          </Badge>
//                        </td>

//                       <td className="text-right">
//                         <div className="flex flex-wrap justify-end gap-2">
//                           <button
//                             type="button"
//                             onClick={() => setExpandedId(expanded ? null : rowKey)}
//                             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//                           >
//                             <Info className="h-4 w-4" />
//                             View
//                           </button>

//                           <button
//                             type="button"
//                             onClick={() => openProductDetails(r)}
//                             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
//                           >
//                             <Eye className="h-4 w-4" />
//                             Details
//                           </button>

//                           {tab === "assigned" ? (
//                             <button
//                               type="button"
//                               onClick={() => requestUnassign(r)}
//                               className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition
//                                 ${
//                                   Number(r.p_status) === 1
//                                     ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
//                                     : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
//                                 }
//                               `}
//                             >
//                               {Number(r.p_status) === 1 ? (
//                                 <>
//                                   <XCircle className="h-4 w-4" />
//                                   Unassign
//                                 </>
//                               ) : (
//                                 <>
//                                   <CheckCircle2 className="h-4 w-4" />
//                                   Assign
//                                 </>
//                               )}
//                             </button>

//                           ) : (
//                             <button
//                               type="button"
//                               onClick={() => requestAssign(r)}
//                               className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
//                             >
//                               <CheckCircle2 className="h-4 w-4" />
//                               Assign
//                             </button>
//                           )}
//                         </div>
//                       </td>
//                     </tr>

//                     {/* Expanded details row */}
//                     {expanded ? (
//                       <tr className="border-b bg-slate-50/60">
//                         <td colSpan={7} className="py-4">
//                           <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
//                             <DetailRow icon={Hash} label="HSN Code" value={p.hsn_code} />
//                             <DetailRow icon={Tag} label="Zoho ID" value={p.zoho_id} />
//                             <DetailRow icon={CalendarDays} label="Validity (Days)" value={p.validity} />
//                             <DetailRow icon={Package} label="Claims" value={p.claims} />
//                             <DetailRow icon={Layers} label="Categories" value={(p.categories || []).map((c) => c?.name).filter(Boolean).join(", ") || "—"} />
//                             {tab === "assigned" ? (
//                               <DetailRow icon={Percent} label="Company Margin" value={marginLabel(p, r.margin)} />
//                             ) : (
//                               <DetailRow icon={Percent} label="Company Margin" value="—" />
//                             )}
//                             <div className="md:col-span-3 rounded-2xl border border-blue-100 bg-white p-4">
//                               <p className="text-xs font-semibold text-slate-500">Features</p>
//                               <p className="mt-1 text-sm font-semibold text-slate-900">{fmt(p.features)}</p>
//                             </div>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : null}
//                   </React.Fragment>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>

//         {totalPages > 1 && (
//           <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
//             <div className="text-xs font-semibold text-slate-500">
//               Page <span className="font-semibold text-slate-900">{currentPage}</span> of{" "}
//               <span className="font-semibold text-slate-900">{totalPages}</span>
//             </div>
//             <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
//           </div>
//         )}
//       </div>

//       {/* Details modal */}
//       <ProductDetailsModal
//         open={openDetails}
//         selected={selected}
//         tab={tab}
//         onClose={() => {
//           setOpenDetails(false);
//           setSelected(null);
//         }}
//         mrpLabel={mrpLabel}
//         marginLabel={marginLabel}
//         rangeLabel={rangeLabel}
//         requestAssign={requestAssign}
//         requestUnassign={requestUnassign}
//       />

//       {/* Confirm modal */}
//       <ConfirmActionModal
//         open={confirmOpen}
//         action={confirmAction}
//         onClose={() => {
//           setConfirmOpen(false);
//           setConfirmAction(null);
//         }}
//         onConfirm={runConfirm}
//       />

//     </div>
//   );
// }



// import React, { useEffect, useMemo, useState } from "react";
// import { useLocation, useParams } from "react-router-dom";
// import Pagination from "../../../Component/Common/Pagination";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { Badge } from "../../../Component/RetailerMainComponents/Badge";

// import {
//   Search,
//   ChevronDown,
//   CheckCircle2,
//   XCircle,
//   Eye,
//   AlertTriangle,
//   Package,
//   Tag,
//   Percent,
//   IndianRupee,
//   Info,
//   ChevronRight,
//   ChevronUp,
//   CalendarDays,
//   Hash,
// } from "lucide-react";

// function classNames(...x) {
//   return x.filter(Boolean).join(" ");
// }
// const money = (n) => `₹${Number(n || 0).toLocaleString()}`;
// const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

// function Modal({ open, title, onClose, children, max = "max-w-3xl" }) {
//   if (!open) return null;
//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
//       <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
//       <div className={classNames("relative w-full rounded-2xl border border-blue-100 bg-white shadow-xl", max)}>
//         <div className="flex items-center justify-between gap-3 border-b border-blue-100 p-4">
//           <p className="text-base font-semibold text-slate-900">{title}</p>
//           <button
//             type="button"
//             onClick={onClose}
//             className="inline-flex items-center justify-center rounded-xl border border-blue-100 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"
//           >
//             <XCircle className="h-4 w-4" />
//           </button>
//         </div>
//         <div className="p-4">{children}</div>
//       </div>
//     </div>
//   );
// }

// function Chip({ active, children, onClick }) {
//   return (
//     <button
//       type="button"
//       onClick={onClick}
//       className={classNames(
//         "whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition",
//         active ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
//       )}
//     >
//       {children}
//     </button>
//   );
// }

// function ProductImg({ src, name }) {
//   return (
//     <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl border border-blue-100 bg-blue-50">
//       {src ? <img src={src} alt={name} className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-blue-700" />}
//     </div>
//   );
// }

// function statusBadgeVariantPublished(status) {
//   return Number(status) === 1 ? "success" : "warning";
// }
// function companyActiveBadge(p_status) {
//   return Number(p_status) === 1 ? "success" : "danger";
// }

// function PricingTypeLabel(p) {
//   const isPercent = Number(p?.is_percent) === 1;
//   const isFixed = Number(p?.is_fixed) === 1;
//   return isPercent ? "Percent" : isFixed ? "Fixed" : "—";
// }
// function mrpLabel(p) {
//   const isPercent = Number(p?.is_percent) === 1;
//   return isPercent ? `${fmt(p?.mrp)}%` : money(p?.mrp);
// }
// function marginLabel(p, margin) {
//   const isPercent = Number(p?.is_percent) === 1;
//   return isPercent ? `${fmt(margin)}%` : money(margin);
// }
// function rangeLabel(p) {
//   return `${money(p?.min_value)} – ${money(p?.max_value)}`;
// }

// function DetailRow({ icon: Icon, label, value }) {
//   return (
//     <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-white p-3">
//       <div className="rounded-xl bg-blue-50 p-2 text-blue-700">
//         <Icon className="h-4 w-4" />
//       </div>
//       <div className="min-w-0">
//         <p className="text-xs font-semibold text-slate-500">{label}</p>
//         <p className="break-words text-sm font-semibold text-slate-900">{fmt(value)}</p>
//       </div>
//     </div>
//   );
// }

// export default function AssignCompanyProducts() {
//   const { id } = useParams();
//   const location = useLocation();

//   const companyObj = location.state?.c || location.state?.company || null;
//   const companyName = companyObj?.business_name || companyObj?.name || location.state?.companyName || "Company";

//   // ✅ mock (replace with API)
//   const [rows, setRows] = useState(() => [
//     {
//       company_product_id: 3,
//       product_id: 72,
//       company_id: 5868,
//       margin: 12,
//       p_status: 0,
//       product: {
//         id: 72,
//         name: "screen damage iphone",
//         image: null,
//         zoho_id: null,
//         hsn_code: null,
//         validity: 120,
//         claims: 12,
//         min_value: 1,
//         max_value: 10000,
//         features: "test",
//         created_at: "2025-12-12T06:49:27.000000Z",
//         updated_at: "2025-12-12T06:49:27.000000Z",
//         is_fixed: 0,
//         is_percent: 1,
//         mrp: "12",
//         status: 1,
//       },
//     },
//     {
//       company_product_id: 4,
//       product_id: 70,
//       company_id: 5868,
//       margin: 300,
//       p_status: null,
//       product: {
//         id: 70,
//         name: "Android Protecti99",
//         image: "https://example.com/product-image.jpg",
//         zoho_id: "588149000009332015",
//         hsn_code: "998413",
//         validity: 780,
//         claims: 24,
//         min_value: 15000,
//         max_value: 50000,
//         features: "Covers accidental damage and theft",
//         created_at: "2025-12-08T07:19:29.000000Z",
//         updated_at: "2025-12-08T07:19:29.000000Z",
//         is_fixed: 1,
//         is_percent: 0,
//         mrp: "300",
//         status: 1,
//       },
//     },
//   ]);

//   // Tabs
//   const [tab, setTab] = useState("assigned");
//   // Filters
//   const [search, setSearch] = useState("");
//   const [published, setPublished] = useState("all");
//   // Pagination
//   const [pageSize, setPageSize] = useState(10);
//   const [currentPage, setCurrentPage] = useState(1);

//   // Expand row (minimal table → full details)
//   const [expandedId, setExpandedId] = useState(null);

//   // Details Modal
//   const [openDetails, setOpenDetails] = useState(false);
//   const [selected, setSelected] = useState(null);

//   // Confirm Modal
//   const [confirmOpen, setConfirmOpen] = useState(false);
//   const [confirmAction, setConfirmAction] = useState(null);

//   useEffect(() => setCurrentPage(1), [tab, search, published, pageSize]);

//   const assigned = useMemo(() => rows.filter((r) => Number(r.p_status) === 1), [rows]);
//   const unassigned = useMemo(() => rows.filter((r) => Number(r.p_status) !== 1), [rows]);

//   const workingSet = tab === "assigned" ? assigned : unassigned;

//   const filtered = useMemo(() => {
//     const q = search.trim().toLowerCase();
//     return workingSet.filter((r) => {
//       const p = r.product || {};
//       if (published === "published" && Number(p.status) !== 1) return false;
//       if (published === "unpublished" && Number(p.status) === 1) return false;

//       if (q) {
//         const hay = [p.name, p.hsn_code, p.zoho_id, r.product_id, r.company_product_id, p.features]
//           .filter(Boolean)
//           .join(" ")
//           .toLowerCase();
//         if (!hay.includes(q)) return false;
//       }
//       return true;
//     });
//   }, [workingSet, search, published]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
//   const pageRows = useMemo(() => {
//     const start = (currentPage - 1) * pageSize;
//     return filtered.slice(start, start + pageSize);
//   }, [filtered, currentPage, pageSize]);

//   const pCounts = useMemo(() => ({ assigned: assigned.length, unassigned: unassigned.length }), [assigned.length, unassigned.length]);

//   const requestAssign = (row) => {
//     setConfirmAction({ type: "assign", row });
//     setConfirmOpen(true);
//   };
//   const requestUnassign = (row) => {
//     setConfirmAction({ type: "unassign", row });
//     setConfirmOpen(true);
//   };

//   const runConfirm = () => {
//     if (!confirmAction?.row) return;
//     const { type, row } = confirmAction;

//     setRows((prev) =>
//       prev.map((x) =>
//         x.company_product_id === row.company_product_id ? { ...x, p_status: type === "assign" ? 1 : 0 } : x
//       )
//     );

//     setConfirmOpen(false);
//     setConfirmAction(null);
//     setExpandedId(null);
//   };

//   const openProductDetails = (row) => {
//     setSelected(row);
//     setOpenDetails(true);
//   };

//   return (
//     <div className="mx-auto space-y-4 p-4 md:p-8">
//       {/* Tabs + counters */}
//       <div className="flex justify-between gap-2 overflow-x-auto rounded-2xl border border-blue-100 bg-white p-2">
//         <div className="flex gap-2">
//           <Chip active={tab === "assigned"} onClick={() => setTab("assigned")}>
//             Assigned
//           </Chip>
//           <Chip active={tab === "unassigned"} onClick={() => setTab("unassigned")}>
//             Not Assigned
//           </Chip>
//         </div>

//         <div className="flex flex-wrap items-center gap-2">
//           <span className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
//             Assigned: {pCounts.assigned}
//           </span>
//           <span className="rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
//             Not Assigned: {pCounts.unassigned}
//           </span>
//           <RefreshButton />
//         </div>
//       </div>

//       {/* Toolbar */}
//       <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
//         <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
//           <div className="md:col-span-6">
//             <label className="mb-1 block text-xs font-semibold text-slate-600">Search</label>
//             <div className="relative">
//               <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
//               <input
//                 value={search}
//                 onChange={(e) => setSearch(e.target.value)}
//                 placeholder="Search product / HSN / Zoho / features..."
//                 className="w-full rounded-xl border border-blue-100 bg-white py-2 pl-10 pr-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
//               />
//             </div>
//           </div>

//           <div className="md:col-span-3">
//             <label className="mb-1 block text-xs font-semibold text-slate-600">Published</label>
//             <div className="relative">
//               <select
//                 value={published}
//                 onChange={(e) => setPublished(e.target.value)}
//                 className="w-full appearance-none rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
//               >
//                 <option value="all">All</option>
//                 <option value="published">Published Only</option>
//                 <option value="unpublished">Unpublished Only</option>
//               </select>
//               <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
//             </div>
//           </div>

//           <div className="md:col-span-2">
//             <label className="mb-1 block text-xs font-semibold text-slate-600">Page Size</label>
//             <div className="relative">
//               <select
//                 value={pageSize}
//                 onChange={(e) => setPageSize(Number(e.target.value))}
//                 className="w-full appearance-none rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
//               >
//                 <option value={5}>5</option>
//                 <option value={10}>10</option>
//                 <option value={20}>20</option>
//                 <option value={50}>50</option>
//               </select>
//               <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
//             </div>
//           </div>

//           <div className="md:col-span-1 flex items-end">
//             <button
//               type="button"
//               onClick={() => {
//                 setSearch("");
//                 setPublished("all");
//               }}
//               className="w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//             >
//               Clear
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Minimal Table */}
//       <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
//         <div className="mb-3">
//           <p className="text-base font-semibold text-slate-900">
//             Products • <span className="text-blue-700">{companyName}</span>
//           </p>
//           <p className="mt-1 text-xs font-semibold text-slate-500">
//             Showing <span className="font-semibold text-slate-900">{filtered.length}</span> results
//           </p>
//         </div>

//         <div className="overflow-x-auto">
//           <table className="w-full text-sm">
//             <thead>
//               <tr className="border-b text-left text-slate-700">
//                 <th className="py-3">Product</th>
//                 <th>Type</th>
//                 <th>MRP</th>
//                 <th>Margin</th>
//                 <th>Range</th>
//                 <th>Published</th>
//                 <th>Company</th>
//                 <th className="text-right">Action</th>
//               </tr>
//             </thead>

//             <tbody>
//               {pageRows.length === 0 ? (
//                 <tr>
//                   <td colSpan={8} className="py-10 text-center text-sm font-semibold text-slate-500">
//                     No products found.
//                   </td>
//                 </tr>
//               ) : null}

//               {pageRows.map((r) => {
//                 const p = r.product || {};
//                 const rowId = r.company_product_id;
//                 const expanded = expandedId === rowId;

//                 return (
//                   <React.Fragment key={rowId}>
//                     <tr className="border-b hover:bg-slate-50">
//                       {/* Product */}
//                       <td className="py-3">
//                         <div className="flex items-center gap-3">
//                           <button
//                             type="button"
//                             onClick={() => setExpandedId(expanded ? null : rowId)}
//                             className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-2 py-2 text-blue-700 hover:bg-blue-50"
//                             title="Expand details"
//                           >
//                             {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
//                           </button>

//                           <ProductImg src={p.image} name={p.name} />
//                           <div className="min-w-0">
//                             <p className="truncate text-sm font-semibold text-slate-900">{fmt(p.name)}</p>
//                             <p className="mt-0.5 text-xs font-semibold text-slate-500">
//                               PID: <span className="font-semibold">{r.product_id}</span> • CPID:{" "}
//                               <span className="font-semibold">{r.company_product_id}</span>
//                             </p>
//                           </div>
//                         </div>
//                       </td>

//                       {/* Type */}
//                       <td className="font-semibold text-slate-900">{PricingTypeLabel(p)}</td>

//                       {/* MRP */}
//                       <td className="font-semibold text-slate-900">{mrpLabel(p)}</td>

//                       {/* Margin */}
//                       <td className="font-semibold text-slate-900">{marginLabel(p, r.margin)}</td>

//                       {/* Range (compact) */}
//                       <td className="font-semibold text-slate-700">
//                         <span className="rounded-xl border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
//                           {rangeLabel(p)}
//                         </span>
//                       </td>

//                       {/* Published */}
//                       <td>
//                         <Badge variant={statusBadgeVariantPublished(p.status)}>
//                           {Number(p.status) === 1 ? "Published" : "Unpublished"}
//                         </Badge>
//                       </td>

//                       {/* Company Status */}
//                       <td>
//                         <Badge variant={companyActiveBadge(r.p_status)}>
//                           {Number(r.p_status) === 1 ? "Active" : "Inactive"}
//                         </Badge>
//                       </td>

//                       {/* Actions */}
//                       <td className="text-right">
//                         <div className="flex flex-wrap justify-end gap-2">
//                           <button
//                             type="button"
//                             onClick={() => setExpandedId(expanded ? null : rowId)}
//                             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//                           >
//                             <Info className="h-4 w-4" />
//                             View
//                           </button>

//                           <button
//                             type="button"
//                             onClick={() => openProductDetails(r)}
//                             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
//                           >
//                             <Eye className="h-4 w-4" />
//                             Details
//                           </button>

//                           {tab === "assigned" ? (
//                             <button
//                               type="button"
//                               onClick={() => requestUnassign(r)}
//                               className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-100"
//                             >
//                               <XCircle className="h-4 w-4" />
//                               Unassign
//                             </button>
//                           ) : (
//                             <button
//                               type="button"
//                               onClick={() => requestAssign(r)}
//                               className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
//                             >
//                               <CheckCircle2 className="h-4 w-4" />
//                               Assign
//                             </button>
//                           )}
//                         </div>
//                       </td>
//                     </tr>

//                     {/* ✅ Expanded row shows ALL extra details (clean cards) */}
//                     {expanded ? (
//                       <tr className="border-b bg-slate-50/60">
//                         <td colSpan={8} className="py-4">
//                           <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
//                             <DetailRow icon={Hash} label="HSN Code" value={p.hsn_code} />
//                             <DetailRow icon={Tag} label="Zoho ID" value={p.zoho_id} />
//                             <DetailRow icon={CalendarDays} label="Validity (Days)" value={p.validity} />
//                             <DetailRow icon={Package} label="Claims" value={p.claims} />
//                             <DetailRow icon={IndianRupee} label="Min Value" value={money(p.min_value)} />
//                             <DetailRow icon={IndianRupee} label="Max Value" value={money(p.max_value)} />
//                             <div className="md:col-span-3 rounded-2xl border border-blue-100 bg-white p-4">
//                               <p className="text-xs font-semibold text-slate-500">Features</p>
//                               <p className="mt-1 text-sm font-semibold text-slate-900">{fmt(p.features)}</p>
//                             </div>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : null}
//                   </React.Fragment>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>

//         {totalPages > 1 && (
//           <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
//             <div className="text-xs font-semibold text-slate-500">
//               Page <span className="font-semibold text-slate-900">{currentPage}</span> of{" "}
//               <span className="font-semibold text-slate-900">{totalPages}</span>
//             </div>
//             <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
//           </div>
//         )}
//       </div>

//       {/* Details modal (kept) */}
//       <Modal
//         open={openDetails}
//         title="Product Details"
//         onClose={() => {
//           setOpenDetails(false);
//           setSelected(null);
//         }}
//         max="max-w-4xl"
//       >
//         {selected ? (
//           <div className="space-y-4">
//             <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
//               <div className="flex flex-wrap items-start justify-between gap-3">
//                 <div className="flex items-center gap-3">
//                   <ProductImg src={selected.product?.image} name={selected.product?.name} />
//                   <div>
//                     <p className="text-xs font-semibold text-slate-500">Product</p>
//                     <p className="text-base font-semibold text-slate-900">{fmt(selected.product?.name)}</p>
//                     <p className="mt-1 text-xs font-semibold text-slate-600">
//                       PID: <span className="font-semibold">{selected.product_id}</span> • CPID:{" "}
//                       <span className="font-semibold">{selected.company_product_id}</span>
//                     </p>
//                   </div>
//                 </div>

//                 <div className="flex flex-wrap items-center gap-2">
//                   <Badge variant={companyActiveBadge(selected.p_status)}>
//                     {Number(selected.p_status) === 1 ? "Assigned/Active" : "Not Assigned/Inactive"}
//                   </Badge>
//                   <Badge variant={statusBadgeVariantPublished(selected.product?.status)}>
//                     {Number(selected.product?.status) === 1 ? "Published" : "Unpublished"}
//                   </Badge>
//                 </div>
//               </div>
//             </div>

//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <DetailRow icon={Tag} label="Zoho ID" value={selected.product?.zoho_id} />
//               <DetailRow icon={Hash} label="HSN Code" value={selected.product?.hsn_code} />
//               <DetailRow icon={CalendarDays} label="Validity" value={`${fmt(selected.product?.validity)} days`} />
//               <DetailRow icon={Package} label="Claims" value={selected.product?.claims} />

//               <DetailRow icon={IndianRupee} label="MRP" value={mrpLabel(selected.product)} />
//               <DetailRow icon={Percent} label="Margin (Company)" value={marginLabel(selected.product, selected.margin)} />

//               <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-white p-4">
//                 <p className="text-xs font-semibold text-slate-500">Valid Price Range</p>
//                 <p className="mt-1 text-sm font-semibold text-slate-900">{rangeLabel(selected.product)}</p>
//               </div>

//               <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-white p-4">
//                 <p className="text-xs font-semibold text-slate-500">Features</p>
//                 <p className="mt-1 text-sm font-semibold text-slate-900">{fmt(selected.product?.features)}</p>
//               </div>
//             </div>

//             <div className="flex flex-col gap-2 md:flex-row md:justify-end">
//               <button
//                 type="button"
//                 onClick={() => alert("Next: Add Price List flow will be implemented as per your next message.")}
//                 className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//               >
//                 <Tag className="h-4 w-4" />
//                 Add Price List
//               </button>

//               {Number(selected.p_status) === 1 ? (
//                 <button
//                   type="button"
//                   onClick={() => requestUnassign(selected)}
//                   className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
//                 >
//                   <XCircle className="h-4 w-4" />
//                   Unassign
//                 </button>
//               ) : (
//                 <button
//                   type="button"
//                   onClick={() => requestAssign(selected)}
//                   className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
//                 >
//                   <CheckCircle2 className="h-4 w-4" />
//                   Assign
//                 </button>
//               )}
//             </div>
//           </div>
//         ) : null}
//       </Modal>

//       {/* Confirm modal */}
//       <Modal
//         open={confirmOpen}
//         title="Please Confirm"
//         onClose={() => {
//           setConfirmOpen(false);
//           setConfirmAction(null);
//         }}
//         max="max-w-xl"
//       >
//         <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
//           <div className="flex items-start gap-3">
//             <div className="rounded-2xl bg-white p-3 text-blue-700">
//               <AlertTriangle className="h-5 w-5" />
//             </div>
//             <div className="min-w-0">
//               <p className="text-sm font-semibold text-slate-900">
//                 {confirmAction?.type === "assign"
//                   ? "Assign this product to company?"
//                   : "Unassign this product from company?"}
//               </p>
//               <p className="mt-1 text-xs font-semibold text-slate-600">
//                 Product: <span className="font-semibold">{fmt(confirmAction?.row?.product?.name)}</span>
//               </p>
//             </div>
//           </div>
//         </div>

//         <div className="mt-4 flex flex-col gap-2 md:flex-row md:justify-end">
//           <button
//             type="button"
//             onClick={() => {
//               setConfirmOpen(false);
//               setConfirmAction(null);
//             }}
//             className="inline-flex items-center justify-center rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
//           >
//             Cancel
//           </button>

//           <button
//             type="button"
//             onClick={runConfirm}
//             className={classNames(
//               "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white",
//               confirmAction?.type === "assign" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
//             )}
//           >
//             {confirmAction?.type === "assign" ? "Yes, Assign" : "Yes, Unassign"}
//           </button>
//         </div>
//       </Modal>
//     </div>
//   );
// }




// ******************************************************working Code*****************************************************************8


// import { useEffect, useState } from "react";
// import { useParams, useLocation } from "react-router-dom";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import toast from "react-hot-toast";
// import { BASE_URL, Warrenty_URL } from "../../../config/api";

// export default function AssignCompanyProducts({ companyId }) {
//   // const { companyId } = useParams();
//   const location = useLocation();
//   const company = location.state?.company;

//   const [assignedProducts, setAssignedProducts] = useState([]);
//   const [allProducts, setAllProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [tab, setTab] = useState("assigned"); // "assigned" | "notAssigned"

//   // Fetch assigned products
//   const fetchAssignedProducts = async () => {
//     try {
//       const res = await fetch(`${BASE_URL}warranty/company-products?company_id=${companyId}`);
//       const data = await res.json();

//       const products = data?.data?.[0]?.products || [];

//       setAssignedProducts(products);
//     } catch {
//       toast.error("Failed to load assigned products");
//     }
//   };

//   // Fetch all products
//   const fetchAllProducts = async () => {
//     try {
//       const res = await fetch(`${BASE_URL}warranty/products-with-categories`);
//       const data = await res.json();
//       setAllProducts(data.products || []);
//     } catch {
//       toast.error("Failed to load product list");
//     }
//   };

//   // Load both
//   const loadData = async () => {
//     setLoading(true);
//     await Promise.all([fetchAssignedProducts(), fetchAllProducts()]);
//     setLoading(false);
//   };

//   useEffect(() => {
//     loadData();
//   }, []);

//   const assignedIds = new Set(assignedProducts.map((p) => p.id));
//   const notAssignedProducts = allProducts.filter((p) => !assignedIds.has(p.id));

//   // Assign product
//   const assignProduct = async (productId, margin) => {
//     try {
//       const res = await fetch(`${BASE_URL}warranty/assign-product`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ company_id: companyId, product_ids: [productId], margin }),
//       });

//       const data = await res.json();
//       if (data) {
//         toast.success("Product Assigned");
//         loadData();
//       } else toast.error("Failed to assign");
//     } catch {
//       toast.error("Something went wrong");
//     }
//   };

//   // Unassign product
//   const unassignProduct = async (productId, margin) => {
//     try {
//       const res = await fetch(`${BASE_URL}warranty/assign-product/inactive`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ company_id: companyId, product_ids: [productId], margin}),
//       });

//       const data = await res.json();
//       if (data) {
//         toast.success("Product removed");
//         loadData();
//       } else toast.error("Failed to update");
//     } catch {
//       toast.error("Something went wrong");
//     }
//   };

//   return (
//     <div className="space-y-4">
//       <SectionCard title={`Assign Products to ${company?.business_name || "Company"}`}>
//         {/* TAB SWITCH */}
//         <div className="flex gap-2 mb-4">
//           <button
//             className={`px-4 py-2 rounded-lg text-sm ${tab === "assigned" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
//             onClick={() => setTab("assigned")}
//           >
//             Assigned ({assignedProducts.length})
//           </button>

//           <button
//             className={`px-4 py-2 rounded-lg text-sm ${tab === "notAssigned" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
//             onClick={() => setTab("notAssigned")}
//           >
//             Not Assigned ({notAssignedProducts.length})
//           </button>

//           <RefreshButton onClick={loadData} />
//         </div>

//         {/* LOADING */}
//         {loading && <RegularLoader />}

//         {/* ASSIGNED TAB */}
//         {!loading && tab === "assigned" && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-200 text-slate-500">
//                   <th className="py-2 px-2">Product</th>
//                   <th className="py-2 px-2">Category</th>
//                   <th className="py-2 px-2">Expiry Days</th>
//                   <th className="py-2 px-2">Claims</th>
//                   <th className="py-2 px-2">Margin</th>
//                   <th className="py-2 px-2">Status</th>
//                   <th className="py-2 px-2">Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {assignedProducts.length > 0 ? (
//                   assignedProducts.map((p) => (
//                     <tr
//                       key={p.id}
//                       className="border-b border-slate-50 hover:bg-red-50/50"
//                     >
//                       {/* Product */}
//                       <td className="py-2 px-2 font-semibold">{p.name}</td>

//                       {/* Category */}
//                       <td className="py-2 px-2">
//                         {p.categories?.length
//                           ? p.categories.map((cat) => cat.name).join(", ")
//                           : "—"}
//                       </td>

//                       {/* Expiry */}
//                       <td className="py-2 px-2">{p.validity}</td>

//                       {/* Claims */}
//                       <td className="py-2 px-2">{p.claims}</td>

//                       {/* Margin */}
//                       <td className="py-2 px-2">
//                         <div className="flex items-center justify-center gap-1">
//                           {p.is_fixed === 1 && <span>₹</span>}
//                           <span>{p.mrp}</span>
//                           {p.is_percent === 1 && <span>%</span>}
//                         </div>
//                       </td>

//                       <td className="py-2 px-2">
//                         <span
//                           className={`px-2 py-1 text-xs rounded-full font-medium 
//                             ${p.p_status === 1
//                               ? "bg-green-100 text-green-700 border border-green-300"
//                               : "bg-red-100 text-red-700 border border-red-300"
//                             }`}
//                         >
//                           {p.p_status === 1 ? "Active" : "Inactive"}
//                         </span>
//                       </td>


//                       {/* Remove Button */}
//                       <td className="py-2 px-2">
//                         <button
//                           onClick={() => unassignProduct(p.id, p.mrp)}
//                           className="px-3 py-1 text-xs rounded bg-red-50 text-red-600 border border-red-300 hover:bg-red-200"
//                         >
//                           Remove
//                         </button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan="6"
//                       className="text-center py-3 text-slate-500 text-xs"
//                     >
//                       No assigned products.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {/* NOT ASSIGNED TAB */}
//         {!loading && tab === "notAssigned" && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-200 text-slate-500">
//                   <th className="py-2 px-2">Product</th>
//                   <th className="py-2 px-2">Category</th>
//                   <th className="py-2 px-2">Expiry Days</th>
//                   <th className="py-2 px-2">Claims</th>
//                   <th className="py-2 px-2">Margin</th>
//                   <th className="py-2 px-2">Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {notAssignedProducts.length > 0 ? (
//                   notAssignedProducts.map((p) => (
//                     <tr key={p.id} className="border-b border-slate-50 hover:bg-blue-50/50">
//                       <td className="py-2 px-2 font-semibold">{p.name}</td>
//                       <td className="py-2 px-2">
//                         {p.categories?.length ? p.categories.map((cat) => cat.name).join(", ") : "—"}
//                       </td>
//                       <td className="py-2 px-2">{p.validity}</td>
//                       <td className="py-2 px-2">{p.claims}</td>

//                       {/* Margin */}
//                       <td className="py-2 px-2">
//                         <div className="flex items-center justify-center gap-1">
//                           {p.is_fixed === 1 && <span>₹</span>}
//                           <span>{p.mrp}</span>
//                           {p.is_percent === 1 && <span>%</span>}
//                         </div>
//                       </td>

//                       {/* Assign Button */}
//                       <td className="py-2 px-2">
//                         <button
//                           onClick={() => assignProduct(p.id, p.mrp)} // mrp as margin
//                           className="px-3 py-1 text-xs rounded bg-blue-50 text-blue-600 border border-blue-300 hover:bg-blue-200"
//                         >
//                           Assign
//                         </button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="6" className="text-center py-3 text-slate-500 text-xs">
//                       All products assigned.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import { useParams, useLocation } from "react-router-dom";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import toast from "react-hot-toast";
// import { BASE_URL, Warrenty_URL } from "../../../config/api";

// export default function AssignCompanyProducts() {
// const { companyId } = useParams();
// const location = useLocation();
// const company = location.state?.company;

// const [assignedProducts, setAssignedProducts] = useState([]);
// const [allProducts, setAllProducts] = useState([]);
// const [loading, setLoading] = useState(true);
// const [tab, setTab] = useState("assigned"); // "assigned" | "notAssigned"

// const fetchAssignedProducts = async () => {
//     try {
//     const res = await fetch(`${Warrenty_URL}warranty/company-products?company_id=${companyId}`);
//     const data = await res.json();
//     setAssignedProducts(data.data || []);
//     } catch {
//     toast.error("Failed to load assigned products");
//     }
// };

// const fetchAllProducts = async () => {
//     try {
//     const res = await fetch(`${Warrenty_URL}warranty/products-with-categories`);
//     const data = await res.json();
//     setAllProducts(data.products || []);
//     } catch {
//     toast.error("Failed to load product list");
//     }
// };

// const loadData = async () => {
//     setLoading(true);
//     await Promise.all([fetchAssignedProducts(), fetchAllProducts()]);
//     setLoading(false);
// };

// useEffect(() => {
//     loadData();
// }, []);

// const assignedIds = new Set(assignedProducts.map((p) => p.id));

// const notAssignedProducts = allProducts.filter((p) => !assignedIds.has(p.id));

// // ---- Assign Product ----
// const assignProduct = async (productId) => {
//     try {
//     const res = await fetch(`${BASE_URL}warranty/assign-product`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ company_id: companyId, product_id: productId })
//     });

//     const data = await res.json();
//     if (data.status) {
//         toast.success("Product Assigned");
//         loadData();
//     } else toast.error("Failed to assign");
//     } catch {
//     toast.error("Something went wrong");
//     }
// };

// // ---- Unassign Product ----
// const unassignProduct = async (productId) => {
//     try {
//     const res = await fetch(`${BASE_URL}warranty/unassign-product`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ company_id: companyId, product_id: productId })
//     });

//     const data = await res.json();
//     if (data.status) {
//         toast.success("Product removed");
//         loadData();
//     } else toast.error("Failed to update");
//     } catch {
//     toast.error("Something went wrong");
//     }
// };

// return (
//     <div className="space-y-4">
//     <SectionCard title={`Assign Products to ${company?.business_name || "Company"}`}>
        
//         {/* TAB SWITCH */}
//         <div className="flex gap-2 mb-4">
//         <button
//             className={`px-4 py-2 rounded-lg text-sm ${tab === "assigned" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
//             onClick={() => setTab("assigned")}
//         >
//             Assigned ({assignedProducts.length})
//         </button>

//         <button
//             className={`px-4 py-2 rounded-lg text-sm ${tab === "notAssigned" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
//             onClick={() => setTab("notAssigned")}
//         >
//             Not Assigned ({notAssignedProducts.length})
//         </button>

//         <RefreshButton onClick={loadData} />
//         </div>

//         {/* LOADING */}
//         {loading && <RegularLoader />}

//         {/* ASSIGNED TAB */}
//         {!loading && tab === "assigned" && (
//         <div className="space-y-2">
//             {assignedProducts.map((p) => (
//             <div key={p.id} className="flex justify-between items-center p-2 border rounded-md">
//                 <span>{p.name}</span>
//                 <button
//                 className="px-3 py-1 text-xs bg-red-500 text-white rounded"
//                 onClick={() => unassignProduct(p.id)}
//                 >
//                 Remove
//                 </button>
//             </div>
//             ))}
//             {assignedProducts.length === 0 && <p className="text-center text-gray-500 text-sm">No assigned products</p>}
//         </div>
//         )}

//         {/* NOT ASSIGNED TAB */}
//         {/* NOT ASSIGNED TAB */}
//         {/* NOT Assigned Products Table */}
//         {!loading && tab === "notAssigned" && (
//         <div className="overflow-x-auto">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//             <thead>
//                 <tr className="border-b border-slate-200 text-slate-500">
//                 <th className="py-2 px-2">Product</th>
//                 <th className="py-2 px-2">Category</th>
//                 <th className="py-2 px-2">Expiry Days</th>
//                 <th className="py-2 px-2">Claims</th>
//                 <th className="py-2 px-2">Margin</th>
//                 <th className="py-2 px-2">Action</th>
//                 </tr>
//             </thead>

//             <tbody>
//                 {notAssignedProducts.map((p) => (
//                 <tr key={p.id} className="border-b border-slate-50 hover:bg-blue-50/50">
//                     <td className="py-2 px-2 font-semibold">{p.name}</td>

//                     <td className="py-2 px-2">
//                     {p.categories?.length
//                         ? p.categories.map((cat) => cat.name).join(", ")
//                         : "—"}
//                     </td>
//                     <td className="py-2 px-2">{p.validity}</td>
//                     <td className="py-2 px-2">{p.claims}</td>
//                     <td className="py-2 px-2 ">{p.mrp}</td>

//                     <td className="py-2 px-2">
//                     <button
//                         onClick={() => assignProduct(p.id)}
//                         className="px-3 py-1 text-xs rounded bg-blue-50 text-blue-600 border border-blue-300 hover:bg-blue-200"
//                     >
//                         Assign
//                     </button>
//                     </td>
//                 </tr>
//                 ))}

//                 {notAssignedProducts.length === 0 && (
//                 <tr>
//                     <td colSpan="3" className="text-center py-3 text-slate-500 text-xs">
//                     All products assigned.
//                     </td>
//                 </tr>
//                 )}
//             </tbody>
//             </table>
//         </div>
//         )}

//     </SectionCard>
//     </div>
// );
// }
